import { useMemo } from 'react'
import { AUTH_TOKEN_KEY, CUSTOMER_INFORMATION_KEY, DATA_DISTRIBUTOR_KEY, TYPE_ACCOUNT_KEY, USER_NAME_KEY } from '@/core/constants/auth'
import { TCheckBAIDResponse, TGetDistributorInformationResponse } from '@/core/store/distributor.store'

// ============================================
// Storage Helpers
// ============================================

const getStorageItem = <T>(storage: Storage, key: string, defaultValue: T): T => {
    try {
        const item = storage.getItem(key)
        return item ? JSON.parse(item) : defaultValue
    } catch {
        return defaultValue
    }
}

const setStorageItem = <T>(storage: Storage, key: string, value: T): void => {
    storage.setItem(key, JSON.stringify(value))
}

// ============================================
// Custom Hooks
// ============================================

export const useAuthToken = (): string | null => {
    return useMemo(() => localStorage.getItem(AUTH_TOKEN_KEY), [])
}

export const useUsername = (): string => {
    return useMemo(() => localStorage.getItem(USER_NAME_KEY) ?? '', [])
}

export const useDistributorData = () => {
    return useMemo(() => {
        const data = getStorageItem<TCheckBAIDResponse | null>(sessionStorage, DATA_DISTRIBUTOR_KEY, null)
        return {
            baid: data?.baid ?? '',
            name: data?.name ?? '',
            status: data?.status ?? 'unknown',
            joinDate: data?.joinDate ?? '',
            type: data?.type ?? '',
            verify: data?.verify ?? ''
        }
    }, [])
}

export const useCustomerInfo = () => {
    return useMemo(() => {
        const data = getStorageItem<TGetDistributorInformationResponse | null>(sessionStorage, CUSTOMER_INFORMATION_KEY, null)
        return {
            token: data?.token ?? '',
            href: data?.href ?? '',
            status: data?.status ?? ''
        }
    }, [])
}

// ============================================
// Storage Setters
// ============================================

export const saveDistributorData = (data: TCheckBAIDResponse): void => {
    setStorageItem(sessionStorage, DATA_DISTRIBUTOR_KEY, data)
}

export const saveCustomerInfo = (data: TGetDistributorInformationResponse): void => {
    setStorageItem(sessionStorage, CUSTOMER_INFORMATION_KEY, data)
}

export const clearAuthData = (): void => {
    localStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.removeItem(TYPE_ACCOUNT_KEY)
    localStorage.removeItem(USER_NAME_KEY)
    sessionStorage.removeItem(DATA_DISTRIBUTOR_KEY)
    sessionStorage.removeItem(CUSTOMER_INFORMATION_KEY)
}

